"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { useAuth } from "../../../contexts/AuthContext"
import { createPortal } from "react-dom"

const BALANCE_ACCESS_PASSWORD = "Infi@1981"

function BalanceDrawer({ isOpen, onClose, title, children, processing = false }) {
  const drawerContent = (
    <div className={`fixed inset-0 z-[9999] overflow-hidden transition-all duration-300 ${isOpen ? "visible" : "invisible pointer-events-none"}`}>
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-gray-500/30 dark:bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"}`}
        onClick={processing ? undefined : onClose}
      ></div>

      <div className="absolute inset-y-0 right-0 max-w-full flex pl-10">
        <div
          className={`w-screen max-w-md transform transition-transform duration-300 ease-in-out bg-white dark:bg-gray-800 shadow-2xl flex flex-col justify-between ${isOpen ? "translate-x-0" : "translate-x-full"}`}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/50">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  )

  return createPortal(drawerContent, document.body)
}

function AdminRecharge() {
  const navigate = useNavigate()
  const {
    user,
    getAllUsers,
    addBalance,
    removeBalance,
    transferBalance,
    getTransactionHistory,
    getBalanceSummary,
    canAddBalance,
    canRemoveBalance,
    canTransferBalance,
    canViewAllTransactions,
  } = useAuth()

  // State management
  const [users, setUsers] = useState([])
  const [transactions, setTransactions] = useState([])
  const [balanceSummary, setBalanceSummary] = useState({})
  const [currentBalance, setCurrentBalance] = useState(0)
  const [pagination, setPagination] = useState({})
  const [loading, setLoading] = useState(false)
  const [processing, setProcessing] = useState(false)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showRemoveModal, setShowRemoveModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)

  // Add Modal Form states
  const [addFormData, setAddFormData] = useState({ selectedUserId: "", amount: "", description: "" })

  // Remove Modal Form states
  const [removeFormData, setRemoveFormData] = useState({ selectedUserId: "", amount: "", description: "" })

  // Transfer Modal Form states
  const [transferFormData, setTransferFormData] = useState({ transferToUserId: "", amount: "", description: "" })

  // Filter states
  const [filterType, setFilterType] = useState("")
  const [filterUserId, setFilterUserId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [balancePassword, setBalancePassword] = useState("")
  const [balanceAccessError, setBalanceAccessError] = useState("")
  const [balanceUnlocked, setBalanceUnlocked] = useState(false)

  const ITEMS_PER_PAGE = 10
  const isAdminUser = user && ["superadmin", "admin"].includes(user.role)

  // Load initial data
  useEffect(() => {
    if (!isAdminUser || !balanceUnlocked) return

    loadUsers()
    loadBalanceSummary()
    loadTransactionHistory()
  }, [isAdminUser, balanceUnlocked, user?._id])

  useEffect(() => {
    setBalancePassword("")
    setBalanceAccessError("")
    setBalanceUnlocked(false)
  }, [user?._id])

  const loadUsers = async () => {
    const result = await getAllUsers()
    if (result.success) {
      setUsers(result.users)
    }
  }

  const loadTransactionHistory = async (page = 1, type = "", userId = "") => {
    setLoading(true)
    const result = await getTransactionHistory(page, ITEMS_PER_PAGE, type, userId)
    if (result.success) {
      setTransactions(result.data)
      setPagination(result.pagination)
    }
    setLoading(false)
  }

  const loadBalanceSummary = async () => {
    const result = await getBalanceSummary()
    if (result.success) {
      setCurrentBalance(result.currentBalance)
      setBalanceSummary(result.summary)
    }
  }

  const handleAmountKeyDown = (e) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End"
    ];
    if (e.ctrlKey || e.metaKey || allowedKeys.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  }

  const handleAmountChange = (val, setter, currentData) => {
    const clean = val.replace(/,/g, "").replace(/\D/g, "")
    const formatted = clean ? Number(clean).toLocaleString("en-IN") : ""
    setter({
      ...currentData,
      amount: formatted
    })
  }

  const getAmountValue = (value) => Number.parseFloat(String(value || "").replace(/,/g, ""))

  const isValidAmount = (value) => {
    const amount = getAmountValue(value)
    return Number.isFinite(amount) && amount > 0
  }

  const showSuccessAlert = async (message) => {
    await Swal.fire({
      icon: "success",
      title: message,
      timer: 1600,
      showConfirmButton: false,
    })
  }

  const showErrorAlert = async (message) => {
    await Swal.fire({
      icon: "error",
      title: "Transaction failed",
      text: message || "Please try again.",
    })
  }

  const handleBalanceUnlock = (e) => {
    e.preventDefault()

    if (balancePassword === BALANCE_ACCESS_PASSWORD) {
      setBalanceAccessError("")
      setBalancePassword("")
      setBalanceUnlocked(true)
      return
    }

    setBalanceAccessError("Incorrect password. Please try again.")
  }

  const handleAddBalance = async (e) => {
    e.preventDefault()
    if (processing) return
    if (!addFormData.selectedUserId || !isValidAmount(addFormData.amount)) {
      await showErrorAlert("Please select a user and enter a valid amount.")
      return
    }

    try {
      setProcessing(true)
      const result = await addBalance(addFormData.selectedUserId, getAmountValue(addFormData.amount), addFormData.description)
      if (result.success) {
        setAddFormData({ selectedUserId: "", amount: "", description: "" })
        setShowAddModal(false)
        await Promise.all([loadTransactionHistory(currentPage, filterType, filterUserId), loadBalanceSummary(), loadUsers()])
        await showSuccessAlert("Balance added successfully")
      } else {
        await showErrorAlert(result.error)
      }
    } catch (error) {
      await showErrorAlert(error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleRemoveBalance = async (e) => {
    e.preventDefault()
    if (processing) return
    if (!removeFormData.selectedUserId || !isValidAmount(removeFormData.amount)) {
      await showErrorAlert("Please select a user and enter a valid amount.")
      return
    }

    try {
      setProcessing(true)
      const result = await removeBalance(removeFormData.selectedUserId, getAmountValue(removeFormData.amount), removeFormData.description)
      if (result.success) {
        setRemoveFormData({ selectedUserId: "", amount: "", description: "" })
        setShowRemoveModal(false)
        await Promise.all([loadTransactionHistory(currentPage, filterType, filterUserId), loadBalanceSummary(), loadUsers()])
        await showSuccessAlert("Balance removed successfully")
      } else {
        await showErrorAlert(result.error)
      }
    } catch (error) {
      await showErrorAlert(error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleTransferBalance = async (e) => {
    e.preventDefault()
    if (processing) return
    const amount = getAmountValue(transferFormData.amount)
    if (!transferFormData.transferToUserId || !isValidAmount(transferFormData.amount)) {
      await showErrorAlert("Please choose a recipient and enter a valid amount.")
      return
    }
    if (amount > currentBalance) {
      await showErrorAlert("Transfer amount cannot be greater than your current balance.")
      return
    }

    try {
      setProcessing(true)
      const result = await transferBalance(transferFormData.transferToUserId, amount, transferFormData.description)
      if (result.success) {
        setTransferFormData({ transferToUserId: "", amount: "", description: "" })
        setShowTransferModal(false)
        await Promise.all([loadTransactionHistory(currentPage, filterType, filterUserId), loadBalanceSummary(), loadUsers()])
        await showSuccessAlert("Balance transferred successfully")
      } else {
        await showErrorAlert(result.error)
      }
    } catch (error) {
      await showErrorAlert(error.message)
    } finally {
      setProcessing(false)
    }
  }

  const handleFilterChange = () => {
    setCurrentPage(1)
    loadTransactionHistory(1, filterType, filterUserId)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
    loadTransactionHistory(page, filterType, filterUserId)
  }

  const getTransactionBadge = (type) => {
    const colors = {
      add: "bg-emerald-100 text-emerald-800 border border-emerald-200",
      remove: "bg-rose-100 text-rose-800 border border-rose-200",
      transfer: "bg-blue-100 text-blue-800 border border-blue-200",
      refund: "bg-amber-100 text-amber-800 border border-amber-200",
    }
    return colors[type] || "bg-gray-100 text-gray-800 border border-gray-200"
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-IN")
  }

  const currentUserId = user?._id || user?.id
  const transferTargetUsers = users.filter((candidate) => candidate._id !== currentUserId)

  if (!user || !isAdminUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900 dark:text-white">Access Denied</h1>
          <p className="mb-6 text-gray-600 dark:text-gray-300">Admin Balance page is restricted to administrator accounts.</p>
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!balanceUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <form
          onSubmit={handleBalanceUnlock}
          className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700">
            <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2zm10-10V7a4 4 0 0 0-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="mb-2 text-center text-2xl font-bold text-gray-900 dark:text-white">Balance Security</h1>
          <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-300">Enter password to access Balance Management.</p>
          <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-200" htmlFor="balance-password">
            Password
          </label>
          <input
            id="balance-password"
            type="password"
            value={balancePassword}
            onChange={(e) => {
              setBalancePassword(e.target.value)
              setBalanceAccessError("")
            }}
            autoFocus
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-100 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            placeholder="Enter password"
          />
          {balanceAccessError && <p className="mt-3 text-sm font-medium text-red-600">{balanceAccessError}</p>}
          <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700"
            >
              Unlock
            </button>
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50/50 to-yellow-50/30 dark:from-gray-900 dark:to-gray-950 p-6 md:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Page Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-200/50 dark:border-gray-800/50 pb-6">
          <div className="text-left">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Admin Balance Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage user balances, transfers, and view comprehensive transaction history with advanced controls.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 w-full md:w-auto justify-end">
            {canAddBalance() && (
              <button
                type='button'
                onClick={() => setShowAddModal(true)}
                className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200 text-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Balance
              </button>
            )}
            {canRemoveBalance() && (
              <button
                type='button'
                onClick={() => setShowRemoveModal(true)}
                className="flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200 text-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
                Remove Balance
              </button>
            )}
            {canTransferBalance() && (
              <button
                type='button'
                onClick={() => setShowTransferModal(true)}
                className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white px-4 py-2.5 rounded-xl font-bold shadow-md hover:shadow-lg transition-all duration-200 text-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                Transfer Balance
              </button>
            )}
          </div>
        </div>

        {/* Dashboard Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          
          {/* Card 1: Current Balance */}
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-indigo-700 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 text-white text-left flex flex-col justify-between h-36 border border-blue-500/10">
            <div className="absolute top-0 right-0 -mt-3 -mr-3 w-16 h-16 bg-white/10 rounded-full blur-lg pointer-events-none"></div>
            <div className="flex justify-between items-start">
              <span className="text-blue-100 text-xs font-semibold uppercase tracking-wider">Current Balance</span>
              <div className="p-1.5 bg-white/15 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <p className="text-2xl font-bold tracking-tight">{formatCurrency(currentBalance)}</p>
          </div>

          {/* Card 2: Total Added */}
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500 to-teal-650 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 text-white text-left flex flex-col justify-between h-36 border border-emerald-500/10">
            <div className="absolute top-0 right-0 -mt-3 -mr-3 w-16 h-16 bg-white/10 rounded-full blur-lg pointer-events-none"></div>
            <div className="flex justify-between items-start">
              <span className="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Total Added</span>
              <div className="p-1.5 bg-white/15 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{formatCurrency(balanceSummary.add?.totalAmount || 0)}</p>
              <p className="text-[10px] text-emerald-100 mt-1">{balanceSummary.add?.count || 0} transactions</p>
            </div>
          </div>

          {/* Card 3: Total Removed */}
          <div className="relative overflow-hidden bg-gradient-to-br from-rose-500 to-red-650 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 text-white text-left flex flex-col justify-between h-36 border border-rose-500/10">
            <div className="absolute top-0 right-0 -mt-3 -mr-3 w-16 h-16 bg-white/10 rounded-full blur-lg pointer-events-none"></div>
            <div className="flex justify-between items-start">
              <span className="text-rose-100 text-xs font-semibold uppercase tracking-wider">Total Removed</span>
              <div className="p-1.5 bg-white/15 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{formatCurrency(balanceSummary.remove?.totalAmount || 0)}</p>
              <p className="text-[10px] text-rose-100 mt-1">{balanceSummary.remove?.count || 0} transactions</p>
            </div>
          </div>

          {/* Card 4: Total Transfers */}
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-indigo-650 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 text-white text-left flex flex-col justify-between h-36 border border-purple-500/10">
            <div className="absolute top-0 right-0 -mt-3 -mr-3 w-16 h-16 bg-white/10 rounded-full blur-lg pointer-events-none"></div>
            <div className="flex justify-between items-start">
              <span className="text-purple-100 text-xs font-semibold uppercase tracking-wider">Total Transfers</span>
              <div className="p-1.5 bg-white/15 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{formatCurrency(balanceSummary.transfer?.totalAmount || 0)}</p>
              <p className="text-[10px] text-purple-100 mt-1">{balanceSummary.transfer?.count || 0} transactions</p>
            </div>
          </div>

          {/* Card 5: Total Refunds */}
          <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 to-orange-600 p-5 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5 text-white text-left flex flex-col justify-between h-36 border border-amber-500/10">
            <div className="absolute top-0 right-0 -mt-3 -mr-3 w-16 h-16 bg-white/10 rounded-full blur-lg pointer-events-none"></div>
            <div className="flex justify-between items-start">
              <span className="text-amber-100 text-xs font-semibold uppercase tracking-wider">Total Refunds</span>
              <div className="p-1.5 bg-white/15 rounded-lg">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 010 10h-1m-9-10l4-4m-4 4l-4 4" />
                </svg>
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold tracking-tight">{formatCurrency(balanceSummary.refund?.totalAmount || 0)}</p>
              <p className="text-[10px] text-amber-100 mt-1">{balanceSummary.refund?.count || 0} refunds</p>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-left">
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Transaction Type</label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full pl-4 pr-10 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 appearance-none font-semibold h-[48px]"
                >
                  <option value="">All Types</option>
                  <option value="add">Add Balance</option>
                  <option value="remove">Remove Balance</option>
                  <option value="transfer">Transfer</option>
                  <option value="refund">Refund</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
            </div>
            {canViewAllTransactions() && (
              <div className="text-left">
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">User</label>
                <div className="relative">
                  <select
                    value={filterUserId}
                    onChange={(e) => setFilterUserId(e.target.value)}
                    className="w-full pl-4 pr-10 py-3 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 appearance-none font-semibold h-[48px]"
                  >
                    <option value="">All Users</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.username} ({user.email})
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-end">
              <button
                type='button'
                onClick={handleFilterChange}
                className="w-full bg-blue-600 hover:bg-blue-700 active:scale-95 text-white font-bold py-3 px-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 whitespace-nowrap h-[48px] flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* History Table Container */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200/50 dark:border-gray-700/50 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/50 text-left">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">Transaction History</h3>
            <p className="text-sm text-gray-500 mt-1">Showing {ITEMS_PER_PAGE} transactions per page</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200/50">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Performed By
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Old Balance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    New Balance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white/30 divide-y divide-gray-200/30">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Loading transactions...</span>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <svg
                          className="w-12 h-12 text-gray-300 mb-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium">No transactions found</p>
                        <p className="text-sm text-gray-400">Try adjusting your filters or add some transactions</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr
                      key={transaction._id}
                      className={`hover:bg-white/50 transition-colors duration-200 ${index % 2 === 0 ? "bg-white/20" : "bg-white/10"}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatDate(transaction.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getTransactionBadge(transaction.type)}`}
                        >
                          {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {transaction.type === "transfer" ? (
                          <div className="space-y-1">
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">From:</span>
                              <span className="ml-1 font-medium">{transaction.fromUser?.username || "Unknown"}</span>
                            </div>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">To:</span>
                              <span className="ml-1 font-medium">{transaction.toUser?.username || "Unknown"}</span>
                            </div>
                          </div>
                        ) : (
                          <span className="font-medium">{transaction.toUser?.username || "Unknown"}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {transaction.performedBy?.username || "Unknown"}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        <div className="max-w-xs">
                          <p className="truncate font-medium">{transaction.description || "-"}</p>
                          {transaction.type === "refund" && (transaction.campaignName || transaction.templateName) && (
                            <p className="text-xs text-amber-700 font-semibold truncate mt-0.5">
                              Campaign: {transaction.campaignName || transaction.templateName}
                              {transaction.metadata?.refundedCount ? ` (${transaction.metadata.refundedCount} messages)` : ""}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.previousBalance || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                        {formatCurrency(transaction.newBalance)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200/50 bg-gradient-to-r from-gray-50 to-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-semibold">{(pagination.page - 1) * pagination.limit + 1}</span> to{" "}
                <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>{" "}
                of <span className="font-semibold">{pagination.total}</span> results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type='button'
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                  let page = i + 1
                  if (pagination.pages > 5) {
                    if (pagination.page > 3) {
                      page = pagination.page - 2 + i
                    }
                    if (page > pagination.pages) {
                      page = pagination.pages - 4 + i
                    }
                  }
                  return (
                    <button
                      type='button'
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${page === pagination.page
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {page}
                    </button>
                  )
                })}
                <button
                  type='button'
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        <BalanceDrawer isOpen={showAddModal} onClose={() => { setShowAddModal(false); setAddFormData({ selectedUserId: "", amount: "", description: "" }); }} title="Add Balance" processing={processing}>
          <form onSubmit={handleAddBalance} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
              <select
                value={addFormData.selectedUserId}
                onChange={(e) => setAddFormData({ ...addFormData, selectedUserId: e.target.value })}
                disabled={processing}
                required
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choose a user...</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.username} ({user.email}) - {formatCurrency(user.balance || 0)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
                <input
                  type="text"
                  id="amount"
                  placeholder="Enter amount"
                  value={addFormData.amount}
                  onKeyDown={handleAmountKeyDown}
                  onChange={(e) => handleAmountChange(e.target.value, setAddFormData, addFormData)}
                  disabled={processing}
                  required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <textarea
                value={addFormData.description}
                onChange={(e) => setAddFormData({ ...addFormData, description: e.target.value })}
                rows="3"
                disabled={processing}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter description for this transaction"
              />
            </div>
            <button
              type="submit"
              disabled={processing || !addFormData.selectedUserId || !isValidAmount(addFormData.amount)}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-semibold shadow-sm hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Add Balance"
              )}
            </button>
          </form>
        </BalanceDrawer>

        <BalanceDrawer isOpen={showRemoveModal} onClose={() => { setShowRemoveModal(false); setRemoveFormData({ selectedUserId: "", amount: "", description: "" }); }} title="Remove Balance" processing={processing}>
          <form onSubmit={handleRemoveBalance} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select User</label>
              <select
                value={removeFormData.selectedUserId}
                onChange={(e) => setRemoveFormData({ ...removeFormData, selectedUserId: e.target.value })}
                disabled={processing}
                required
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choose a user...</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.username} ({user.email}) - {formatCurrency(user.balance || 0)}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
                <input
                  type="text"
                  id="amount"
                  placeholder="Enter amount to remove"
                  value={removeFormData.amount}
                  onKeyDown={handleAmountKeyDown}
                  onChange={(e) => handleAmountChange(e.target.value, setRemoveFormData, removeFormData)}
                  autoComplete="off"
                  disabled={processing}
                  required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <textarea
                value={removeFormData.description}
                onChange={(e) => setRemoveFormData({ ...removeFormData, description: e.target.value })}
                rows="3"
                disabled={processing}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter description for this transaction"
              />
            </div>
            <button
              type="submit"
              disabled={processing || !removeFormData.selectedUserId || !isValidAmount(removeFormData.amount)}
              className="w-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white py-3 px-6 rounded-xl font-semibold shadow-sm hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Remove Balance"
              )}
            </button>
          </form>
        </BalanceDrawer>

        <BalanceDrawer isOpen={showTransferModal} onClose={() => { setShowTransferModal(false); setTransferFormData({ transferToUserId: "", amount: "", description: "" }); }} title="Transfer Balance" processing={processing}>
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800 text-left">
              Your current balance: <span className="font-semibold">{formatCurrency(currentBalance)}</span>
            </p>
          </div>
          <form onSubmit={handleTransferBalance} className="space-y-4">
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Transfer To</label>
              <select
                value={transferFormData.transferToUserId}
                onChange={(e) => setTransferFormData({ ...transferFormData, transferToUserId: e.target.value })}
                disabled={processing}
                required
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              >
                <option value="">Choose recipient...</option>
                {transferTargetUsers
                  .map((user) => (
                    <option key={user._id} value={user._id}>
                      {user.username} ({user.email}) - {formatCurrency(user.balance || 0)}
                    </option>
                  ))}
              </select>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
                <input
                  type="text"
                  id="txtAmount"
                  placeholder="Enter amount to transfer"
                  value={transferFormData.amount}
                  onKeyDown={handleAmountKeyDown}
                  onChange={(e) => handleAmountChange(e.target.value, setTransferFormData, transferFormData)}
                  autoComplete="off"
                  disabled={processing}
                  required
                  className="w-full pl-9 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
                />
              </div>
            </div>
            <div className="text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (Optional)</label>
              <textarea
                value={transferFormData.description}
                onChange={(e) => setTransferFormData({ ...transferFormData, description: e.target.value })}
                rows="3"
                disabled={processing}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder="Enter description for this transfer"
              />
            </div>
            <button
              type="submit"
              disabled={
                processing ||
                !transferFormData.transferToUserId ||
                !isValidAmount(transferFormData.amount) ||
                getAmountValue(transferFormData.amount) > currentBalance
              }
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-6 rounded-xl font-semibold shadow-sm hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {processing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                "Transfer Balance"
              )}
            </button>
          </form>
        </BalanceDrawer>
      </div>
    </div>
  )
}

export default AdminRecharge
